import { Autocomplete, Box, Typography } from '@mui/material';
import { nanoid } from '@reduxjs/toolkit';
import { FocusEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useLazyGetPlaceDetailsQuery, usePlaceAutocompleteMutation } from '../../apiOther/google';
import { AddressPrediction, ParsedPlace } from '../../apiOther/google.interface';
import { TextField } from '../../styled/inputs';

type Props = {
  name?: string;
  label?: string;
  value: string;
  error?: boolean;
  helperText?: ReactNode;
  required?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  // Fires as the user types (reason 'input'/'clear'), NOT when a prediction is
  // selected — so the parent keeps ownership of the free-typed street value.
  onInputChange: (text: string) => void;
  // Fires once, when a prediction is chosen and its details resolve.
  onPlaceSelected: (place: ParsedPlace) => void;
};

const MIN_CHARS = 3;
const DEBOUNCE_MS = 300;

// AddressAutocomplete is a freeSolo MUI Autocomplete backed by the Places API
// (New) REST endpoints. Typing fetches debounced predictions; picking one resolves
// place details and hands the parsed fields + lat/lng back to the parent. If the
// Places API is disabled the requests fail silently and the field behaves as a
// plain text input (manual entry still works).
export const AddressAutocomplete = ({ name, label, value, error, helperText, required, onBlur, onInputChange, onPlaceSelected }: Props) => {
  const [placeAutocomplete] = usePlaceAutocompleteMutation();
  const [getPlaceDetails] = useLazyGetPlaceDetailsQuery();
  const [options, setOptions] = useState<AddressPrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const sessionToken = useRef<string>(nanoid());
  const timer = useRef<ReturnType<typeof setTimeout>>();

  const schedule = useCallback(
    (input: string) => {
      if (timer.current) clearTimeout(timer.current);
      if (input.trim().length < MIN_CHARS) {
        setOptions([]);
        return;
      }
      timer.current = setTimeout(async () => {
        setLoading(true);
        try {
          const preds = await placeAutocomplete({ input, sessionToken: sessionToken.current }).unwrap();
          setOptions(preds);
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, DEBOUNCE_MS);
    },
    [placeAutocomplete],
  );

  const handleSelect = useCallback(
    async (prediction: AddressPrediction) => {
      try {
        const place = await getPlaceDetails({ placeId: prediction.placeId, sessionToken: sessionToken.current }).unwrap();
        onPlaceSelected(place);
      } catch {
        // Non-fatal: leave the typed text as-is if details fail to resolve.
      } finally {
        // Details closes the session; a new session starts for the next search.
        sessionToken.current = nanoid();
        setOptions([]);
      }
    },
    [getPlaceDetails, onPlaceSelected],
  );

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  return (
    <Autocomplete
      freeSolo
      autoComplete
      blurOnSelect
      filterOptions={(opts) => opts}
      options={options}
      loading={loading}
      inputValue={value}
      noOptionsText="No matches"
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
      onInputChange={(_e, newInput, reason) => {
        // 'reset' fires when a selection or the controlled value updates the input;
        // ignore it so we don't overwrite the parent's value or re-query.
        if (reason === 'reset') return;
        onInputChange(newInput);
        schedule(newInput);
      }}
      onChange={(_e, selected) => {
        if (selected && typeof selected !== 'string') handleSelect(selected);
      }}
      renderOption={(props, option) =>
        typeof option === 'string' ? (
          <li {...props}>{option}</li>
        ) : (
          <li {...props} key={option.placeId}>
            <Box>
              <Typography variant="body2">{option.primaryText}</Typography>
              {option.secondaryText && (
                <Typography variant="caption" color="text.secondary">
                  {option.secondaryText}
                </Typography>
              )}
            </Box>
          </li>
        )
      }
      renderInput={(params) => (
        <TextField
          {...params}
          name={name}
          label={label}
          error={error}
          helperText={helperText}
          onBlur={onBlur}
          fullWidth
          variant="outlined"
          my={2}
          required={required}
        />
      )}
    />
  );
};
